import { useEffect, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { WbGrid, WbGridCol, WbGridRow, WbText } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import VimdtButton from '../Common/VimdtButton';
import VimdtSwitch from '../Common/VimdtSwitch';
import VimdtAccordion from '../Common/VimdtAccordion';
import CollapseAllToggle from '../Common/CollapseAllToggle';
import ImageCompose from './ImageCompose';
import Image360View from './Image360View';
import ImageRenderList from './ImageRenderList';
import './ImageList.scss';

const MIME_TYPES = { jpeg: 'image/jpeg', webp: 'image/webp' };

function ImageList({
	imageList,
	isImagesLoading,
	saveFileName,
	imageType,
	rowCount = 6,
	bulk = false,
	background,
	cropped,
	loadedBackground,
	loadedFileFormat,
}) {
	const { t } = useTranslation();

	const [imagesCount, setImagesCount] = useState(0);
	const [vinsCount, setVinsCount] = useState(0);
	const [saving, setSaving] = useState(false);
	const [renderedList, setRenderedList] = useState({});
	const [showImages, setShowImages] = useState(bulk);
	const [panels, setPanels] = useState([]);
	const [innerPanels, setInnerPanels] = useState(false);
	const [availablePanels, setAvailablePanels] = useState([]);

	useEffect(() => {
		setShowImages(bulk);

		const tempRenderList = {};
		const tmpPanelsList = [];
		let vinsCounter = 0;
		let imagesCounter = 0;

		for (const key of Object.keys(imageList)) {
			vinsCounter += 1;
			if (imageList[key].length > 0 && !('errorMessage' in imageList[key][0])) {
				imagesCounter += imageList[key].length;
			}
			if (imageList[key].length === 0 || imageList[key][0]?.errorMessage) continue;
			tempRenderList[key] = imageList[key];
			tmpPanelsList.push(key);
		}

		setVinsCount(vinsCounter);
		setImagesCount(imagesCounter);
		setAvailablePanels(tmpPanelsList);
		setRenderedList(tempRenderList);
	}, [imageList, bulk]);

	const saveImages = () => {
		setSaving(true);
		const zip = new JSZip();
		const imageFolder = zip.folder(saveFileName);
		const mimeType = MIME_TYPES[imageType] || 'image/png';

		for (const key of Object.keys(imageList)) {
			if (imageList[key].length === 0 || 'errorMessage' in imageList[key][0]) continue;
			imageList[key].forEach(({ title, blob }) => {
				const newBlob = new Blob([blob], { type: mimeType });
				const path = bulk ? `${key}/${title}.${imageType}` : `${title}.${imageType}`;
				imageFolder.file(path, newBlob, { createFolders: true });
			});
		}

		zip
			.generateAsync({ type: 'blob' })
			.then((content) => {
				saveAs(content, `${saveFileName}.zip`);
				setSaving(false);
			})
			.catch((err) => {
				console.error(err);
				setSaving(false);
			});
	};

	const panelHandler = (mode) => {
		if (mode === 0) {
			setPanels(availablePanels);
			setInnerPanels(true);
		} else if (mode === 1) {
			setPanels([]);
			setInnerPanels(false);
		}
	};

	const summary = `${vinsCount} ${t('vimdt.placeholders.vinNumberPlaceholder')}${
		vinsCount > 1 ? 's' : ''
	}, ${imagesCount} ${
		imagesCount > 1 ? t('vimdt.infoTexts.imagesFound') : t('vimdt.infoTexts.imageFound')
	}`;

	const header = (
		<WbGrid fullWidth className="vimdt-image-list__header">
			<WbGridRow>
				<WbGridCol mq1={12} mq3={4}>
					<WbText strong>{summary}</WbText>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={4}>
					<VimdtSwitch
						label={t('vimdt.loadSaveButtons.showImages')}
						onBtn={t('vimdt.components.onButton')}
						offBtn={t('vimdt.components.offButton')}
						status={[t('vimdt.components.onButton'), t('vimdt.components.offButton')]}
						getValue={setShowImages}
						defaultValue={showImages}
						disabled={isImagesLoading}
					/>
				</WbGridCol>
				<WbGridCol mq1={12} mq3={4}>
					<VimdtButton
						label={t('vimdt.loadSaveButtons.saveImages')}
						filledIn={false}
						onClick={saveImages}
						loading={saving}
						disabled={isImagesLoading}
					/>
				</WbGridCol>
			</WbGridRow>
		</WbGrid>
	);

	if (showImages) {
		return <div className="vimdt-image-list">{header}</div>;
	}

	return (
		<div className="vimdt-image-list">
			{header}

			<CollapseAllToggle
				panelHandler={panelHandler}
				panels={panels}
				availablePanels={availablePanels}
			/>

			{!isImagesLoading && imagesCount > 0 && <Image360View images={imageList} />}

			{!isImagesLoading && imagesCount > 0 && (
				<ImageCompose
					images={renderedList}
					saveFileName={`${saveFileName}_COMPOSED`}
					imageType={imageType}
					cropped={cropped}
					loadedBackground={loadedBackground}
					loadedFileFormat={loadedFileFormat}
				/>
			)}

			<VimdtAccordion
				activeKeys={panels}
				onChange={setPanels}
				items={Object.keys(renderedList).map((vin) => ({
					key: vin,
					header: vin,
					content: (
						<ImageRenderList
							vin={vin}
							imageList={renderedList[vin]}
							rowCount={rowCount}
							imageType={imageType}
							background={background}
							isDownload
							innerPanels={innerPanels}
						/>
					),
				}))}
			/>

			<div className="vimdt-image-list__footer">
				<VimdtButton
					label={t('vimdt.loadSaveButtons.saveImages')}
					filledIn={false}
					onClick={saveImages}
					loading={saving}
					disabled={isImagesLoading}
				/>
			</div>
		</div>
	);
}

export default ImageList;
